import React from 'react';
import { RegionCode, getRegion } from '../utils/region';
// Brand mark used site-wide (navbar, mobile menu). Each domain ships ONE
// artwork, exported BLACK — it renders as-is on light surfaces and is inverted
// to white on dark ones.
//
// At the top of the page the mark is plain black (light mode) or white (dark
// mode) at 80%. Once the navbar retracts on scroll it crossfades into brand
// green. The change is a crossfade between two stacked copies of the SAME
// artwork (an <img> and a mask-filled block), so only `opacity` animates —
// that's GPU-composited, with no repaint cost. Animating a CSS filter or color
// instead would re-rasterize the mark on every frame.
const LOGO_BY_REGION: Record<RegionCode, {
  light: string;
  dark: string;
}> = {
  '253': {
    light: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png'
  },
  '206': {
    // Single white vector artwork. SVG stays crisp at every size, unlike the
    // raster PNGs it replaced.
    light: 'https://cdn.magicpatterns.com/uploads/bGxoW4WtWCUnBwpobWRxPH/206LOGO.svg',
    dark: 'https://cdn.magicpatterns.com/uploads/bGxoW4WtWCUnBwpobWRxPH/206LOGO.svg'
  },
  '425': {
    light: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png'
  }
};
/**
 * Resolve the active domain's logo image URL.
 */
export function getLogoSrc(region?: RegionCode, surface: 'light' | 'dark' = 'dark'): string {
  return LOGO_BY_REGION[region ?? getRegion()][surface];
}
// TEMPORARY GLOBAL HIDE — every logo across the site is suppressed
// while this flag is true (the Logo component below, plus the inline
// <img> logos in Navbar.tsx that import this constant). To restore the
// logos, flip this back to `false` and re-publish.
export const LOGOS_HIDDEN = false;
// Untinted state: black at 80% on light surfaces, white at 80% on dark.
const LOGO_OPACITY_CLASS = 'opacity-80';
// Brand green the mark takes on once the navbar retracts, at full strength.
const TINT_COLOR = '#04a05c';
const TINT_OPACITY = 1;
// Length of the crossfade between the plain and green states.
const TINT_FADE_MS = 1200;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  /**
   * The artwork is exported white. Leave this true to invert it to black on
   * light surfaces; pass false for call sites that are ALWAYS dark, so the
   * white artwork is used in both themes.
   */
  invertOnLight?: boolean;
  /** Retained for backwards-compatibility with existing call sites. */
  cycleEvery?: number;
  region?: RegionCode;
  pinned?: boolean;
  /** True once the navbar has retracted on scroll — switches the mark to green. */
  tinted?: boolean;
  tintColor?: string;
}
/**
 * 206Gravel brand mark — plain black/white at the top of the page, brand green
 * once the page is scrolled.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  tinted = false,
  tintColor = TINT_COLOR
}: LogoProps) {
  // While LOGOS_HIDDEN is true, every Logo instance renders nothing.
  // Flip the constant above to `false` (or remove this block) to restore.
  if (LOGOS_HIDDEN) return null;
  const src = getLogoSrc(getRegion(), 'light');
  const fade = 'transition-opacity ease-in-out motion-reduce:transition-none';
  const fadeStyle = {
    transitionDuration: `${TINT_FADE_MS}ms`
  };
  return <span className={`relative inline-flex items-center ${className}`} data-id="element-1174" data-mp="i048wsn5dk87">
      {/* Base artwork — black on light surfaces, white on dark. Fades out as
       the green copy fades in so the mid-transition read stays clean (leaving
       it visible underneath would leave a dark rim on the antialiased edges). */}
      <img src={src} alt="206Gravel" style={fadeStyle} className={`${imgClassName} block pointer-events-none ${fade} ${tinted ? 'opacity-0' : LOGO_OPACITY_CLASS} ${invertOnLight ? 'dark:invert' : 'invert'}`} data-id="element-1175" data-mp="2ypri6lezvsgc" />
      {/* Green copy — the same file used as a CSS mask so it can be tinted
       (an <img> can only be inverted, not colored). Purely decorative: the
       <img> above carries the accessible name. */}
      <span aria-hidden="true" style={{
      ...fadeStyle,
      backgroundColor: tintColor,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'left center',
      maskPosition: 'left center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      opacity: tinted ? TINT_OPACITY : 0
    }} className={`absolute inset-0 pointer-events-none ${fade}`} data-id="element-1176" data-mp="ci3l4ui2oml9" />
    </span>;
}